import { z } from 'zod'
import { TagEventFn, UserConfirmDataFormType } from './types'
import { BreadcrumbItem } from '@/components/Breadcrumb/types'
import { isValidPhone, validateBirthDate } from '@/utils/validations'
import { ERROR_MESSAGES } from '@/modules/Signup/constants'
import { removeNonDigits } from '@/utils/string'

export const valuesValidations = (
  val: Partial<UserConfirmDataFormType>,
  ctx: z.RefinementCtx,
  flagAddress: boolean,
  flagEmergencyContacts: boolean,
  flagPregnancy?: boolean,
  hasPregnancyData?: boolean,
) => {
  if (!val?.birthdate || !validateBirthDate(val.birthdate)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: val?.birthdate
        ? ERROR_MESSAGES.INVALID_DATE
        : ERROR_MESSAGES.REQUIRED_FIELD,
      path: ['birthdate'],
    })
  }

  if (
    val.emergencyContactCellphone.length &&
    flagEmergencyContacts &&
    !isValidPhone(val.emergencyContactCellphone)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Celular inválido',
      path: ['emergencyContactCellphone'],
    })
  }

  if (val.cellphone.length && !isValidPhone(val.cellphone)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Celular inválido',
      path: ['cellphone'],
    })
  }

  if (
    val.emergencyContactFullName.length &&
    !val.emergencyContactFullName
      .trim()
      .match(
        "^[a-zA-ZáàâãéèêíïóôõöúçñÁÀÂÃÉÈÊÍÏÓÔÕÖÚÇÑ']+([ -][a-zA-ZáàâãéèêíïóôõöúçñÁÀÂÃÉÈÊÍÏÓÔÕÖÚÇÑ']+)*$",
      )
  )
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Nome inválido',
      path: ['emergencyContactFullName'],
    })

  if (
    val.emergencyContactFullName.length &&
    !val.emergencyContactFullName
      .trim()
      .match("^[a-zA-ZÀ-ÖØ-öø-ÿ']{2,}(?:\\s[a-zA-ZÀ-ÖØ-öø-ÿ-']+)+$")
  )
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Insira nome e sobrenome',
      path: ['emergencyContactFullName'],
    })

  const formattedCellPhone = removeNonDigits(val?.cellphone)
  const formattedyContactCellphone = removeNonDigits(
    val?.emergencyContactCellphone,
  )
  if (formattedCellPhone === formattedyContactCellphone) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'Os telefones do titular e do contato de emergência devem ser diferentes.',
      path: ['emergencyContactCellphone'],
    })
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'Os telefones do titular e do contato de emergência devem ser diferentes.',
      path: ['cellphone'],
    })
  }

  if (flagAddress) {
    if (val.cep.length < 9)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: val.cep.length === 0 ? 'Campo obrigatório' : 'CEP inválido',
        path: ['cep'],
      })
    if (!val.streetName.length)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Campo obrigatório',
        path: ['streetName'],
      })
    if (!val.number.length)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Campo obrigatório',
        path: ['number'],
      })
    if (!val.city.length)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Campo obrigatório',
        path: ['city'],
      })
    if (!val.state.length)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Campo obrigatório',
        path: ['state'],
      })
    if (!val.neighborhood.length)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Campo obrigatório',
        path: ['neighborhood'],
      })
  }
  if (flagEmergencyContacts) {
    if (val.emergencyContactCellphone.replace(/[()-\s]/g, '').length < 11)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          val.emergencyContactCellphone.length === 0
            ? 'Campo obrigatório'
            : 'Celular inválido',
        path: ['emergencyContactCellphone'],
      })
    if (!val.emergencyContactFullName.length)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Campo obrigatório',
        path: ['emergencyContactFullName'],
      })
    if (!val.relationship.length)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Campo obrigatório',
        path: ['relationship'],
      })
  }
  if (flagPregnancy && !hasPregnancyData) {
    if (!val?.daysPregnancy?.length) {
      ctx.addIssue({
        code: z?.ZodIssueCode?.custom,
        message: 'Campo obrigatório',
        path: ['daysPregnancy'],
      })
    }
    if (!val?.weeksPregnancy?.length) {
      ctx.addIssue({
        code: z?.ZodIssueCode?.custom,
        message: 'Campo obrigatório',
        path: ['weeksPregnancy'],
      })
    }
    if (!val?.lastMenstrualPeriodDate) {
      ctx.addIssue({
        code: z?.ZodIssueCode?.custom,
        message: 'Campo obrigatório',
        path: ['lastMenstrualPeriodDate'],
      })
    }
  }
}

export const formattedCellphone = cellphone => {
  if (!cellphone) return ''
  const rawCellphone = cellphone.trim().replace(' ', '')
  let cellphoneFmt = ''
  if (rawCellphone.length === 14) {
    return cellphone
  }
  if (rawCellphone.length === 11) {
    const areaCode = rawCellphone.slice(0, 2)
    const prefix = rawCellphone.slice(2, 6)
    const suffix = rawCellphone.slice(6)
    cellphoneFmt = `(${areaCode}) ${prefix}-${suffix}`

    return cellphoneFmt
  } else {
    return cellphone
  }
}
export const phoneRegex =
  /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/

export const getBreadcrumbsFromNoPreferenceFlow = (
  speciality: string,
  isMobile?: boolean,
  logAnalyticsEvent?: TagEventFn,
): BreadcrumbItem[] => {
  return [
    {
      route: `/`,
      label: 'Início',
    },
    {
      route: `/atendimento-agendado/preferencia/${speciality}`,
      label: 'Preferência',
    },
    {
      route: `/atendimento-agendado/${speciality}/agendamento-sem-preferencia`,
      label: 'Agendamento sem Preferência',
    },
    {
      route: '/atendimento-agendado/anexos',
      label: 'Anexos',
      onClick: () => {
        if (isMobile) logAnalyticsEvent('Pac_Agendada_Voltar')
      },
    },
    {
      route: `/atendimento-agendado/confirmar-dados`,
      label: 'Dados',
      active: true,
    },
  ]
}

export const getBreadcrumbsFromSpecialityFlux = (
  speciality: string,
  medicId: number | string,
  specialityId: number | string,
  isMobile?: boolean,
  logAnalyticsEvent?: TagEventFn,
): BreadcrumbItem[] => {
  return [
    {
      route: `/`,
      label: 'Início',
    },
    {
      route: `/atendimento-agendado/preferencia/${speciality}`,
      label: 'Preferência',
    },
    {
      route: `/atendimento-agendado/preferencia/${speciality}/especialidades`,
      label: 'Especialidade',
    },
    {
      route: `/atendimento-agendado/preferencia/busca-profissional/${speciality}/${specialityId}`,
      label: 'Buscar pelo nome do profissional',
    },
    {
      route: `/atendimento-agendado/categoria/${speciality}/${medicId}`,
      label: 'Data e Hora',
    },
    {
      route: '/atendimento-agendado/anexos',
      label: 'Anexos',
      onClick: () => {
        if (isMobile) logAnalyticsEvent('Pac_Agendada_Voltar')
      },
    },
    {
      route: `/atendimento-agendado/confirmar-dados`,
      label: 'Dados',
      active: true,
    },
  ]
}

export const getBreadcrumbsFromMedicFlux = (
  speciality: string,
  medicId: number | string,
  isMobile?: boolean,
  logAnalyticsEvent?: TagEventFn,
): BreadcrumbItem[] => {
  return [
    {
      route: `/`,
      label: 'Início',
    },
    {
      route: `/atendimento-agendado/preferencia/${speciality}`,
      label: 'Preferência',
    },
    {
      route: `/atendimento-agendado/preferencia/busca-profissional/${speciality}`,
      label: 'Buscar pelo nome do profissional',
    },
    {
      route: `/atendimento-agendado/categoria/${speciality}/${medicId}`,
      label: 'Data e Hora',
    },
    {
      route: '/atendimento-agendado/anexos',
      label: 'Anexos',
      onClick: () => {
        if (isMobile) logAnalyticsEvent('Pac_Agendada_Voltar')
      },
    },
    {
      route: `/atendimento-agendado/confirmar-dados`,
      label: 'Dados',
      active: true,
    },
  ]
}

export const getImmediateServiceBreadcrumbWithoutScreening = (
  logAnalyticsEvent: TagEventFn,
): BreadcrumbItem[] => {
  return [
    {
      route: '/',
      label: 'Início',
      onClick: () => {
        logAnalyticsEvent('Pac_Imediata_Voltar')
      },
    },
    {
      route: '/atualizar-dados',
      label: 'Seus dados',
      active: true,
    },
  ]
}
